import type { ErrorContext, Middleware } from "../clients/slashid-api"
import { isNetworkError } from "../domain/errors.internal"
import { sleep } from "./time"

const RETRY_COUNT_HEADER = "SlashID-SdkRetryCount"

export type ExponentialBackoffOptions = {
    maxRetries: number
    baseDelayMs: number
}

function getRetryCount(init: RequestInit): number {
    if (!init.headers) {
        return 0
    }

    const headers = new Headers(init.headers)
    const retryCountHeader = headers.get(RETRY_COUNT_HEADER)
    return retryCountHeader ? parseInt(retryCountHeader) : 0
}

export function createRetryOnNetworkErrorMiddleware({
    baseDelayMs,
    maxRetries,
}: ExponentialBackoffOptions): Middleware {
    // alternative: pass context through init (SlashID-SDK-retry header) or error
    // alternative: wrapper on top of the client, proxy to retry
    // cache (url, init) => retries - clear on success/max retries after throwing an error

    return {
        onError: async ({ error, response, url, init, fetch: fetchApi }: ErrorContext) => {
            if (isNetworkError(error, response)) {
                const retryCount = getRetryCount(init)

                if (retryCount >= maxRetries) {
                    return response
                }

                await sleep(Math.pow(2, retryCount) * baseDelayMs)

                return await fetchApi(url, {
                    ...init,
                    headers: { ...init.headers, [RETRY_COUNT_HEADER]: `${retryCount + 1}` },
                })
            }

            return response
        },
    }
}
